import React from 'react';
import { useInView } from '@/hooks/useInView';

const SARA_PHOTO = 'https://customer-assets.emergentagent.com/job_sara-ads-consultant/artifacts/mn6a55v8_unnamed.jpg';
const ABOUT_VIDEO = 'https://customer-assets.emergentagent.com/job_ff005761-8ac3-4660-9dad-114860e1014d/artifacts/7zq1z6k9_749e7808b16f4e178caeca8ed6638d7b.mp4';

const FloatingMetric = ({ label, value, className }) => (
  <div className={`bg-card rounded-xl shadow-[var(--shadow-card)] p-3 border border-border ${className}`}>
    <p className="text-primary text-[11px] font-medium leading-tight">{label}</p>
    <p className="text-foreground text-lg font-bold leading-tight">{value}</p>
  </div>
);

export const AboutSection = () => {
  const [ref, isInView] = useInView();

  return (
    <section data-testid="about-section" className="py-16 lg:py-24 bg-background" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Left - Photo with metrics */}
          <div className={`transition-all duration-700 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <div className="relative max-w-sm mx-auto lg:mx-0 py-6 px-6">
              <div className="rounded-2xl overflow-hidden shadow-[var(--shadow-elevated)] border border-border relative">
                <img
                  src={SARA_PHOTO}
                  alt="Sara - Google Ads Consultant"
                  className="w-full object-cover aspect-[3/4]"
                  loading="lazy"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-hero/80 via-hero/40 to-transparent p-6 pt-16">
                  <p className="text-[hsl(220_15%_95%)] font-semibold text-center text-lg">
                    Work with Sara today
                  </p>
                </div>
              </div>

              {/* Floating metrics */}
              <FloatingMetric
                label="Accounts Managed"
                value="360+"
                className="absolute -top-1 -left-1 sm:top-0 sm:-left-2 animate-float"
              />
              <FloatingMetric
                label="Annual Ad Spend"
                value="$9M+"
                className="absolute -top-1 -right-1 sm:top-0 sm:-right-2 animate-float [animation-delay:0.5s]"
              />
              <FloatingMetric
                label="PPC Experience"
                value="8 Years"
                className="absolute -bottom-1 left-1/2 -translate-x-1/2 sm:bottom-0 animate-float [animation-delay:1s]"
              />
            </div>
          </div>

          {/* Right - About text */}
          <div className={`transition-all duration-700 delay-200 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <p className="text-primary font-semibold text-sm uppercase tracking-widest mb-3">
              Hi, It's Me.
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-6">
              About Sara
            </h2>
            <div className="space-y-5 text-muted-foreground leading-relaxed">
              <p>
                Over the past three years, I've managed and optimized more than 360 Google Ads accounts,
                overseeing $9M+ in annual ad spend as both an agency strategist and a former Google representative.
                Working on both sides of the ecosystem has given me rare access to insider perspectives,
                direct platform insights, and a deep understanding of how Google's algorithm actually behaves
                in the real world.
              </p>
              <p>
                Today, I use that expertise not to reduce churn for agencies or ad platforms, but to help
                businesses win. By applying the strategies, patterns, and data signals I've learned from
                inside the system, I help companies generate stronger returns on their ad spend and turn
                Google Ads into a reliable, scalable revenue engine.
              </p>
            </div>
          </div>
        </div>

        {/* Video below */}
        <div className={`mt-16 max-w-4xl mx-auto transition-all duration-700 delay-[400ms] ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="rounded-xl overflow-hidden shadow-[var(--shadow-elevated)] bg-foreground/5">
            <video
              controls
              preload="metadata"
              className="w-full aspect-video"
              playsInline
              poster={SARA_PHOTO}
            >
              <source src={ABOUT_VIDEO} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </div>
    </section>
  );
};
